import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, AdminBody } from "./components/AdminHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PROFILES, REG_STATUSES, STATUS_COLORS } from "@/lib/constants";
import { Download, Trash2, Search, Users, Ticket, Mail, AlertTriangle, ExternalLink, BellRing } from "lucide-react";

const RegistrationsAdmin = () => {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [profile, setProfile] = useState("all");
  const [edition, setEdition] = useState("all");
  const [tab, setTab] = useState("participants"); // participants | interest
  const [detailId, setDetailId] = useState(null);

  const { data: editions = [] } = useQuery({ queryKey: ["admin-editions"], queryFn: async () => (await api.get("/admin/editions")).data });
  const edName = (id) => editions.find((x) => x.id === id)?.date_label || "—";

  const params = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status !== "all") p.set("status", status);
    if (profile !== "all") p.set("profile", profile);
    if (edition !== "all") p.set("edition_id", edition);
    if (tab === "interest") p.set("interest", "only");
    return p.toString();
  };

  const { data: regs = [], isLoading } = useQuery({
    queryKey: ["registrations", q, status, profile, edition, tab],
    queryFn: async () => (await api.get(`/admin/registrations?${params()}`)).data,
  });

  const { data: detail } = useQuery({
    queryKey: ["reg-detail", detailId],
    queryFn: async () => (await api.get(`/admin/registrations/${detailId}`)).data,
    enabled: !!detailId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["registrations"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["reg-detail"] });
  };

  const setStatusM = useMutation({
    mutationFn: async ({ id, s, sendEmail, cascade = true, message = "" }) =>
      api.put(`/admin/registrations/${id}/status`, { status: s, send_email: !!sendEmail, cascade_group: cascade, message }),
    onSuccess: (r, v) => {
      invalidate();
      const errs = r?.data?.email_errors || [];
      if (errs.length) toast.error(`Statut : ${v.s} — e-mail NON envoyé : ${errs[0]}`, { duration: 8000 });
      else toast.success(`Statut : ${v.s}`);
    },
    onError: () => toast.error("Erreur"),
  });
  const del = useMutation({ mutationFn: async (id) => api.delete(`/admin/registrations/${id}`), onSuccess: () => { invalidate(); toast.success("Supprimé"); } });
  const resend = useMutation({
    mutationFn: async ({ id, type }) => api.post(`/admin/registrations/${id}/resend`, { type }),
    onSuccess: () => toast.success("E-mail envoyé"),
    onError: (e) => toast.error(e?.response?.data?.detail || "Erreur"),
  });
  const remind = useMutation({
    mutationFn: async () => api.post("/admin/reminder", { edition_id: edition !== "all" ? edition : editions.find((e) => e.is_active)?.id, when_label: "" }),
    onSuccess: (r) => toast.success(`Rappel envoyé à ${r.data.sent} confirmé(s)`),
  });

  const exportCsv = () => window.open(`${API}/admin/registrations.csv${edition !== "all" ? `?edition_id=${edition}` : ""}`, "_blank");

  return (
    <>
      <AdminHeader title="Inscriptions" subtitle="Validation, billets, accompagnants et doublons">
        <Button variant="outline" onClick={() => remind.mutate()} data-testid="send-reminder"><BellRing size={16} className="mr-1" /> Rappel</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv"><Download size={16} className="mr-1" /> CSV</Button>
      </AdminHeader>
      <AdminBody>
        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("participants")} className={`px-4 py-2 text-sm border ${tab === "participants" ? "bg-f2b-black text-white border-f2b-black" : "bg-white border-f2b-filet text-f2b-gray"}`} data-testid="tab-participants">Participants</button>
          <button onClick={() => setTab("interest")} className={`px-4 py-2 text-sm border ${tab === "interest" ? "bg-f2b-black text-white border-f2b-black" : "bg-white border-f2b-filet text-f2b-gray"}`} data-testid="tab-interest">Intéressés (prochaine édition)</button>
        </div>

        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative max-w-xs flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-f2b-gray" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, société, billet…" className="pl-9" data-testid="reg-search" />
          </div>
          <Select value={edition} onValueChange={setEdition}><SelectTrigger className="w-40" data-testid="reg-edition-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes éditions</SelectItem>{editions.map((e) => <SelectItem key={e.id} value={e.id}>{e.date_label}</SelectItem>)}</SelectContent>
          </Select>
          {tab === "participants" && (
            <>
              <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="reg-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{REG_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={profile} onValueChange={setProfile}><SelectTrigger className="w-52" data-testid="reg-profile-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous les profils</SelectItem>{PROFILES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
        </div>

        <div className="bg-white border border-f2b-filet overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-f2b-light border-b border-f2b-filet"><tr>
              {["Participant", "Société", "Profil", "Édition", "Statut", "Billet", "Check-in", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium text-f2b-gray">{h}</th>)}
            </tr></thead>
            <tbody>
              {isLoading ? <tr><td colSpan={8} className="px-4 py-10 text-center text-f2b-gray">Chargement…</td></tr>
                : regs.length === 0 ? <tr><td colSpan={8} className="px-4 py-10 text-center text-f2b-gray">Aucun résultat.</td></tr>
                : regs.map((r) => (
                  <tr key={r.id} className="border-b border-f2b-filet last:border-0 hover:bg-f2b-light/40 cursor-pointer" data-testid={`reg-${r.id}`} onClick={() => setDetailId(r.id)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-f2b-text">{r.first_name} {r.last_name}</span>
                        {r.is_primary === false && <Badge variant="secondary" className="text-xs">Accompagnant</Badge>}
                        {r.suspicious_duplicate && <span title={r.suspicious_reason}><AlertTriangle size={14} className="text-f2b-gold" /></span>}
                      </div>
                      <span className="text-xs text-f2b-gray">{r.email}</span>
                    </td>
                    <td className="px-4 py-3 text-f2b-gray">{r.company || "—"}</td>
                    <td className="px-4 py-3 text-f2b-gray">{r.profile_type || "—"}</td>
                    <td className="px-4 py-3 text-f2b-gray">{edName(r.edition_id)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {tab === "participants" ? (
                        <Select value={r.status} onValueChange={(s) => setStatusM.mutate({ id: r.id, s })}>
                          <SelectTrigger className="h-8 w-40" data-testid={`reg-status-${r.id}`}><SelectValue><Badge className={STATUS_COLORS[r.status]} variant="secondary">{r.status}</Badge></SelectValue></SelectTrigger>
                          <SelectContent>{REG_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <Badge className={STATUS_COLORS[r.status]} variant="secondary">{r.status}</Badge>}
                    </td>
                    <td className="px-4 py-3">{r.ticket_number ? <a href={`/billet/${r.qr_token}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-f2b-cyan text-xs flex items-center gap-1">{r.ticket_number}<ExternalLink size={12} /></a> : <span className="text-f2b-gray">—</span>}</td>
                    <td className="px-4 py-3 text-f2b-gray text-xs">{r.checkin_at ? new Date(r.checkin_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => { if (window.confirm("Supprimer ?")) del.mutate(r.id); }} className="text-f2b-gray hover:text-red-500 p-1" data-testid={`reg-delete-${r.id}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </AdminBody>

      {/* Detail sheet */}
      <Sheet open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="reg-detail-sheet">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-display text-2xl">{detail.first_name} {detail.last_name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-5 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[detail.status]} variant="secondary">{detail.status}</Badge>
                  {detail.is_primary === false && <Badge variant="outline">Accompagnant</Badge>}
                  {detail.suspicious_duplicate && <Badge className="bg-f2b-gold/15 text-f2b-gold" variant="secondary">Doublon suspect : {detail.suspicious_reason}</Badge>}
                </div>

                <dl className="grid grid-cols-3 gap-y-2 text-f2b-text">
                  {[["E-mail", detail.email], ["Téléphone", detail.phone], ["Société", detail.company], ["Fonction", detail.function], ["Profil", detail.profile_type + (detail.profile_other ? ` (${detail.profile_other})` : "")], ["Ville", detail.city], ["Édition", edName(detail.edition_id)], ["Demande", new Date(detail.created_at).toLocaleString("fr-FR")], ["Source", detail.source], ["Groupe", detail.group_id], ["Billet", detail.ticket_number || "—"], ["Email confirmation", detail.confirmation_email_sent ? "Envoyé" : "Non"], ["Check-in", detail.checkin_at ? new Date(detail.checkin_at).toLocaleString("fr-FR") : "—"], ["RGPD", detail.gdpr_consent ? "Consenti" : "Non"]].map(([k, v]) => (
                    <React.Fragment key={k}><dt className="col-span-1 text-f2b-gray">{k}</dt><dd className="col-span-2 break-words">{v || "—"}</dd></React.Fragment>
                  ))}
                </dl>

                {detail.group?.length > 0 && (
                  <div className="border border-f2b-filet p-3">
                    <p className="f2b-kicker text-f2b-gray mb-2 flex items-center gap-1"><Users size={14} /> Groupe</p>
                    {detail.group.map((g) => (
                      <button key={g.id} onClick={() => setDetailId(g.id)} className="block text-left text-f2b-cyan hover:underline">{g.first_name} {g.last_name} — {g.status}</button>
                    ))}
                  </div>
                )}

                <div>
                  <p className="f2b-kicker text-f2b-gray mb-2">Validation</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" className="bg-f2b-cyan text-f2b-black hover:bg-f2b-cyan/90" onClick={() => setStatusM.mutate({ id: detail.id, s: "Confirmé" })} data-testid="detail-confirm">Confirmer</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatusM.mutate({ id: detail.id, s: "Liste d'attente" })} data-testid="detail-waitlist">Liste d'attente</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatusM.mutate({ id: detail.id, s: "Refusé" })}>Refuser</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatusM.mutate({ id: detail.id, s: "Annulé" })}>Annuler</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatusM.mutate({ id: detail.id, s: "Présent", cascade: false })}>Marquer présent</Button>
                    <Button size="sm" variant="outline" onClick={() => setStatusM.mutate({ id: detail.id, s: "Absent", cascade: false })}>Marquer absent</Button>
                  </div>
                </div>

                <div>
                  <p className="f2b-kicker text-f2b-gray mb-2">Emails</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => resend.mutate({ id: detail.id, type: "confirmation" })} data-testid="detail-resend-confirmation"><Mail size={14} className="mr-1" /> Renvoyer confirmation</Button>
                    <Button size="sm" variant="outline" onClick={() => resend.mutate({ id: detail.id, type: "ticket" })} data-testid="detail-resend-ticket"><Ticket size={14} className="mr-1" /> Renvoyer billet</Button>
                  </div>
                  {detail.qr_token && <a href={`/billet/${detail.qr_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-f2b-cyan text-sm mt-3">Voir le billet <ExternalLink size={13} /></a>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default RegistrationsAdmin;
